import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const MIN_SCALE = 1;
const MAX_SCALE = 4;

/**
 * ZoomableImage — inline pan/zoom viewer.
 * • Mouse wheel = zoom centered on cursor
 * • Click and drag when zoomed = pan
 * • Two-finger pinch on touch = zoom, single-finger drag when zoomed = pan
 * • Double-click / double-tap = toggle 1x ↔ 2.5x
 * • Snaps back to origin when scale returns to 1
 */
export const ZoomableImage = ({ src, alt, className }) => {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const pinch = useRef(null); // { startDist, startScale, cx, cy }

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Reset internal state when the image itself changes
  useEffect(() => {
    reset();
  }, [src, reset]);

  // Clamp pan so the image doesn't drift entirely off-screen.
  const clamp = (nextScale, x, y) => {
    const el = wrapRef.current;
    if (!el) return { x, y };
    const w = el.clientWidth;
    const h = el.clientHeight;
    // How far we can pan before edges cross center: (scale-1) * halfDimension
    const maxX = ((nextScale - 1) * w) / 2;
    const maxY = ((nextScale - 1) * h) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  const zoomAt = (delta, clientX, clientY) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Offset of cursor from the wrapper center
    const cx = clientX - rect.left - rect.width / 2;
    const cy = clientY - rect.top - rect.height / 2;
    setScale((s) => {
      const nextRaw = s * (1 + delta);
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextRaw));
      if (next === s) return s;
      // Keep the point under the cursor stationary.
      const ratio = next / s;
      setTx((prev) => {
        const nx = ratio * (prev - cx) + cx;
        return clamp(next, nx, ty).x;
      });
      setTy((prev) => {
        const ny = ratio * (prev - cy) + cy;
        return clamp(next, tx, ny).y;
      });
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return next;
    });
  };

  const onWheel = (e) => {
    e.preventDefault();
    zoomAt(-e.deltaY * 0.0015, e.clientX, e.clientY);
  };

  const onDoubleClick = (e) => {
    if (scale > 1) {
      reset();
    } else {
      zoomAt(1.5, e.clientX, e.clientY); // 1 → 2.5x
    }
  };

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY, tx, ty };
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const nx = dragStart.current.tx + (e.clientX - dragStart.current.x);
    const ny = dragStart.current.ty + (e.clientY - dragStart.current.y);
    const { x, y } = clamp(scale, nx, ny);
    setTx(x);
    setTy(y);
  };

  const onMouseUp = () => {
    dragging.current = false;
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      pinch.current = {
        startDist: Math.hypot(dx, dy),
        startScale: scale,
        cx: (a.clientX + b.clientX) / 2,
        cy: (a.clientY + b.clientY) / 2,
      };
    } else if (e.touches.length === 1 && scale > 1) {
      dragging.current = true;
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      e.preventDefault();
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const nextRaw = pinch.current.startScale * (dist / pinch.current.startDist);
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextRaw));
      setScale(next);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
    } else if (e.touches.length === 1 && dragging.current) {
      e.preventDefault();
      const nx = dragStart.current.tx + (e.touches[0].clientX - dragStart.current.x);
      const ny = dragStart.current.ty + (e.touches[0].clientY - dragStart.current.y);
      const { x, y } = clamp(scale, nx, ny);
      setTx(x);
      setTy(y);
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinch.current = null;
    if (e.touches.length === 0) dragging.current = false;
  };

  const zoomed = scale > 1.01;

  return (
    <div
      ref={wrapRef}
      onWheel={onWheel}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'relative w-full h-full flex items-center justify-center overflow-hidden select-none touch-none',
        zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
        className
      )}
      data-testid="zoomable-image"
      data-scale={scale.toFixed(2)}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transition: dragging.current || pinch.current ? 'none' : 'transform 0.2s ease-out',
        }}
        className="max-w-full max-h-full object-contain shadow-2xl will-change-transform"
      />
      {zoomed && (
        <div className="absolute bottom-4 right-4 px-3 py-1.5 text-xs uppercase tracking-[0.14em] bg-cream/90 text-ink rounded-full backdrop-blur-sm">
          {scale.toFixed(1)}× · double-click to reset
        </div>
      )}
    </div>
  );
};

export default ZoomableImage;
